import { useState } from 'react'

import {
  Modal,
  ModalProvider,
  PrimaryButton,
  SecondaryButton,
  useSteps,
} from '@joor/design-system'
import styled from 'styled-components/macro'

import usePortal from 'hooks/usePortal'

import { StepsModalPayload } from '../../AddressesWidget'
import { AddressesSubmitParams } from '../../addressesWidget.hooks'
import { MismatchContent } from './components/MismatchContent'
import { WarningContent } from './components/WarningContent'
import {
  formatShippingAddressToProvidedAddress,
  formatShippingAddressValuesToProvidedAddress,
} from 'features/Orders/Review/utils/addresses.utils'

const TOTAL_STEPS = 2

export function UpdateAddressStepsModal({
  payload,
  isSubmitting,
  onSubmit,
  onClose,
}: Props) {
  const { currentStep, nextStep } = useSteps(TOTAL_STEPS)

  const Portal = usePortal()

  const [overrideShipping, setOverrideShipping] = useState<boolean | null>(null)

  const submitShipping = (override: boolean | null) => {
    return onSubmit({
      params: { shipping: payload.params!.shipping!, billing: undefined },
      overrideShipping: Boolean(override),
      overrideBilling: false,
    })
  }

  const submitBilling = (override: boolean | null) => {
    return onSubmit({
      params: { shipping: undefined, billing: payload.params!.billing! },
      overrideBilling: Boolean(override),
      overrideShipping: false,
    })
  }

  const handleSubmit = (lastStepOverride: boolean | null) => {
    // this means we had a primary location in the shipping
    // and the user clicked CANCEL on the modal FIRST STEP
    if (overrideShipping === null) {
      return submitBilling(lastStepOverride)
    }

    // this means we had a primary location in the billing
    // and the user clicked CANCEL on the modal SECOND STEP
    if (lastStepOverride === null) {
      return submitShipping(overrideShipping)
    }

    // if we reach this point we want to submit both
    return onSubmit({
      params: payload.params,
      overrideShipping: overrideShipping,
      overrideBilling: lastStepOverride,
    })
  }

  const shippingStep =
    payload.types.shipping === 'csd'
      ? {
          title: 'Shipping address mismatch',
          component: (
            <MismatchContent
              csdAddress={formatShippingAddressToProvidedAddress(
                payload.csdAddresses!.shipping!,
              )}
              editedAddress={formatShippingAddressValuesToProvidedAddress(
                payload.params!.shipping!.values,
              )}
            />
          ),
          action: (
            <>
              <SecondaryButton
                onClick={() => {
                  setOverrideShipping(false)
                  nextStep()
                }}
                loading={isSubmitting}
              >
                This order only
              </SecondaryButton>
              <PrimaryButton
                onClick={() => {
                  setOverrideShipping(true)
                  nextStep()
                }}
                loading={isSubmitting}
              >
                This order and account
              </PrimaryButton>
            </>
          ),
        }
      : {
          title: 'Shipping updates local to this order',
          component: <WarningContent isBrand />,
          action: (
            <>
              <SecondaryButton
                onClick={() => {
                  setOverrideShipping(null)
                  nextStep()
                }}
              >
                Don't update
              </SecondaryButton>
              <PrimaryButton
                onClick={() => {
                  setOverrideShipping(false)
                  nextStep()
                }}
                loading={isSubmitting}
              >
                Continue
              </PrimaryButton>
            </>
          ),
        }

  const billingStep =
    payload.types.billing === 'csd'
      ? {
          title: 'Billing address mismatch',
          component: (
            <MismatchContent
              csdAddress={formatShippingAddressToProvidedAddress(
                payload.csdAddresses!.billing!,
              )}
              editedAddress={formatShippingAddressValuesToProvidedAddress(
                payload.params!.billing!.values,
              )}
            />
          ),
          action: (
            <>
              <SecondaryButton
                onClick={() => {
                  handleSubmit(false)
                }}
                loading={isSubmitting}
              >
                This order only
              </SecondaryButton>
              <PrimaryButton
                onClick={() => {
                  handleSubmit(true)
                }}
                loading={isSubmitting}
              >
                This order and account
              </PrimaryButton>
            </>
          ),
        }
      : {
          title: 'Billing updates local to this order',
          component: <WarningContent isBrand />,
          action: (
            <>
              <SecondaryButton
                onClick={() => {
                  handleSubmit(null)
                }}
              >
                Don't update
              </SecondaryButton>
              <PrimaryButton
                onClick={() => {
                  handleSubmit(false)
                }}
                loading={isSubmitting}
              >
                Continue
              </PrimaryButton>
            </>
          ),
        }

  const steps = [shippingStep, billingStep]

  return (
    <Portal>
      <ModalProvider backgroundComponent={StyledBackgroundComponent}>
        <Modal
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
          title={{
            text: steps[currentStep].title,
            iconName: 'warning',
          }}
          isOpen
          size="medium"
          onClose={onClose}
        >
          <Modal.Content>{steps[currentStep].component}</Modal.Content>
          <Modal.Actions>
            <ActionsWrapper>{steps[currentStep].action}</ActionsWrapper>
          </Modal.Actions>
        </Modal>
      </ModalProvider>
    </Portal>
  )
}

type Props = {
  payload: StepsModalPayload
  isSubmitting: boolean
  onSubmit: ({
    params,
    overrideShipping,
    overrideBilling,
  }: {
    params: AddressesSubmitParams
    overrideShipping: boolean
    overrideBilling: boolean
  }) => Promise<void>
  onClose: () => void
}

const StyledBackgroundComponent = styled.div`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 99999;
  backdrop-filter: blur(3px);
  background-color: rgba(0, 0, 0, 0);
  align-items: center;
  justify-content: center;
`

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  align-items: center;
`
